/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Label, Loader, Spacer } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { ServiceShortInitialState } from "~/components/Services/initialState";
import { ServiceAttachment } from "~/components/Services/types";
import { ARAttachedServices } from "~/models/assignmentRule";
import { ServiceGroup } from "~/models/serviceGroup";
import { formatServiceName, useAxios } from "~/tools";
import { ServicePreview } from "./ServicePreview";

type ServiceSelectProps = {
  serviceAttachments?: ServiceAttachment[] | ARAttachedServices[];
  setSelectedId: React.Dispatch<React.SetStateAction<string>>;
  selectedId: string;
};

function ServiceSelect({
  serviceAttachments,
  setSelectedId,
  selectedId,
}: ServiceSelectProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [services, setServices] = React.useState([ServiceShortInitialState]);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const selectedServicesIds = serviceAttachments?.map(
    (item: ServiceAttachment | ARAttachedServices) => item.service.id
  );
  const filterServices = services?.filter(
    (service: ServiceGroup | { id: string; name: string }) => {
      return !selectedServicesIds?.find((id: string) => id === service.id);
    }
  );

  const fetchServices = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/search/services`);
      setServices(data.searchResults);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, showError]);

  React.useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const getValue = () =>
    filterServices
      ?.filter((element) => (element?.id === selectedId ? element : null))
      .map(({ id, name }) => ({
        value: id,
        label: name,
      })) || {};

  const filteredServices =
    filterServices?.map((serviceItem) => ({
      value: serviceItem.id,
      label: formatServiceName(serviceItem),
    })) || [];

  return (
    <>
      <Fieldset>
        <Label name="Service" htmlFor="selectService" />
        {isLoading ? (
          <Loader inContent />
        ) : (
          <>
            <ServicePreview
              inputId="selectService"
              getValue={getValue}
              filteredServices={filteredServices}
              setSelectedId={setSelectedId}
            />
          </>
        )}
      </Fieldset>
      <Spacer height="1rem" />
    </>
  );
}

export { ServiceSelect };
